import {
  Box,
  DialogContent,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import { useState, useEffect } from 'react';
import PersonAddIcon from '@mui/icons-material/Person';
import { useContractsWithSubscribers } from '../../../../../hooks/useContracts';

/**
 * Tab para asignar contrato al proyecto
 * Permite seleccionar un contrato y muestra los suscriptores asociados
 */
const ContractTab = ({
  localProject,
  onContractChange,
}) => {
  const { data: contractsWithSubscribers = [], isLoading } = useContractsWithSubscribers();
  const [selectedContract, setSelectedContract] = useState(null);

  useEffect(() => {
    if (localProject?.uuid_contract && contractsWithSubscribers.length > 0) {
      const contract = contractsWithSubscribers.find(c => c.uuid === localProject.uuid_contract);
      if (contract && contract !== selectedContract) {
        setSelectedContract(contract);
      }
    } else if (!localProject?.uuid_contract) {
      setSelectedContract(null);
    }
  }, [localProject?.uuid_contract, contractsWithSubscribers, selectedContract]);

  const handleContractChange = (event, newValue) => {
    setSelectedContract(newValue);
    onContractChange?.(newValue);
  };

  const selectedSubscribers = selectedContract?.subscribers || [];

  return (
    <Box sx={{ width: '100%' }}>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ width: '100%' }}>
          {/* Selector de contrato */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Seleccionar Contrato
            </Typography>
            
            <Autocomplete
              value={selectedContract}
              onChange={handleContractChange}
              options={contractsWithSubscribers}
              getOptionLabel={(option) => `${option.contract_number} - ${option.subscribers.map(s => `${s.first_name} ${s.last_name}`).join(', ')}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar contrato por número o nombre de suscriptor"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonAddIcon sx={{ color: 'action.active', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Contrato #{option.contract_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.subscribers.map(s => `${s.first_name} ${s.last_name}`).join(', ')}
                    </Typography>
                  </Box>
                </Box>
              )}
              loading={isLoading}
              loadingText="Cargando contratos..."
              noOptionsText="No se encontraron contratos"
              isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    opacity: 0.8,
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 3,
                    opacity: 0.6,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 3,
                  },
                },
              }}
            />
          </Box>

          {/* Información del contrato seleccionado */}
          {selectedContract && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Contrato Seleccionado
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  Contrato #{selectedContract.contract_number}
                </Typography>
              </Alert>

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Suscriptores del Contrato:
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSubscribers.map((subscriber) => (
                  <Chip
                    key={subscriber.uuid}
                    label={`${subscriber.first_name} ${subscriber.last_name}`}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontWeight: 'bold',
                      '& .MuiChip-label': {
                        px: 2
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Mensaje cuando no hay contrato seleccionado */}
          {!selectedContract && (
            <Alert severity="warning">
              <Typography>
                Debes seleccionar un contrato para asociarlo al proyecto.
              </Typography>
            </Alert>
          )}

          {/* Mensaje cuando el contrato seleccionado no tiene suscriptores */}
          {selectedContract && selectedSubscribers.length === 0 && (
            <Alert severity="error">
              <Typography>
                El contrato seleccionado no tiene suscriptores asociados.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Box>
  );
};

export default ContractTab;
